// Meta Pixel + Conversions API helpers for CMM 2026
// - Browser: fbq() with shared `eventID` for de-duplication.
// - Server: POST /api/meta/capi/event mirrors the same `event_id`.
// Only PII passed to browser goes through Meta's built-in AM hashing (em/ph/fn/ln/ct).

import axios from "axios";

export const PIXEL_ID = "610768654683927";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readCookie = (name) => {
  if (typeof document === "undefined") return "";
  const row = document.cookie.split("; ").find((c) => c.startsWith(name + "="));
  return row ? decodeURIComponent(row.split("=").slice(1).join("=")) : "";
};

const digitsOnly = (v) => (v ? String(v).replace(/\D/g, "") : "");

// Re-init Meta Pixel with Advanced Matching for the current user.
// Meta hashes these client-side. Only pass values Meta expects; keep everything lowercase/trimmed.
export const mpInit = (user = {}) => {
  if (typeof window === "undefined" || !window.fbq) return;
  const am = { country: "in" };
  if (user.email) am.em = String(user.email).trim().toLowerCase();
  if (user.phone) am.ph = digitsOnly(user.phone);
  if (user.first_name) am.fn = String(user.first_name).trim().toLowerCase();
  if (user.last_name) am.ln = String(user.last_name).trim().toLowerCase();
  if (user.city) am.ct = String(user.city).trim().toLowerCase();
  try { window.fbq("init", PIXEL_ID, am); } catch { /* noop */ }
};

// Fire a fbq event with an optional shared event_id for de-dup with server-side CAPI.
export const mpTrack = (event_name, custom_data = {}, event_id) => {
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    if (event_id) window.fbq("track", event_name, custom_data, { eventID: event_id });
    else window.fbq("track", event_name, custom_data);
  } catch { /* noop */ }
};

// POST to backend which forwards to Meta CAPI with hashed PII + IP/UA/fbp/fbc.
export const mpCapi = async ({ event_name, event_id, custom_data = {}, user_data = {} }) => {
  try {
    await axios.post(`${API}/meta/capi/event`, {
      event_name,
      event_id,
      event_source_url: typeof window !== "undefined" ? window.location.href : "",
      custom_data,
      user_data,
      fbp: readCookie("_fbp"),
      fbc: readCookie("_fbc"),
    }, { timeout: 6000 });
  } catch { /* CAPI failure never blocks UX */ }
};

// Combined conversion: fires browser Pixel AND queues server-side CAPI with the SAME event_id.
// Returns the generated (or passed) event_id.
export const trackConversion = (event_name, {
  event_id,
  custom_data = {},
  user_data = {},
} = {}) => {
  const eid = event_id || `${event_name.toLowerCase()}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  // Advanced matching re-init when we know who the user is (registrations, purchases, leads)
  if (user_data && (user_data.email || user_data.phone)) mpInit(user_data);
  mpTrack(event_name, custom_data, eid);
  mpCapi({ event_name, event_id: eid, custom_data, user_data });
  return eid;
};
